import Loading from '@/components/Loading';
import { message } from '@/components/Message';
import { NumericKeyboardInput } from '@/components/NumericKeyboard';
import OrderDescriptions from '@/components/OrderDescriptions';
import PosButton from '@/components/PosButton';
import { Appointment } from '@/models/appointment';
import { DateTimeFormat, TimeMinutesFormat } from '@/models/common';
import { PreOrder } from '@/models/order';
import { startService } from '@/models/preOrder';
import { getTotalDuratonBySKUSpecifications } from '@/models/sku';
import { UserFullInfo, useUserFullInfo } from '@/models/user';
import { playSound } from '@/utils/sound';
import { MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Modal, Space, Typography } from 'antd';
import { APIError } from 'api/types';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

/**
 * 对齐到上下一个 {minutes} 分钟整点
 * @param baseTime 基准时间
 * @param forward 对齐方向
 * @param offsetMinutes 初始的分钟偏移量
 * @param stepMinutes 步长，例如 15则对齐到刻钟
 * @returns 分钟差值（相对当前时刻）
 */
const stepMinutes = (baseTime: Dayjs, forward: boolean, offsetMinutes: number, stepMinutes: number) => {
  let end = baseTime.clone().add(offsetMinutes, 'minute');
  const remainder = end.minute() % stepMinutes;
  if (forward) {
    end = end.add(stepMinutes - remainder, 'minute'); // 补到下一个时刻
  } else {
    end = end.subtract(remainder || stepMinutes, 'minute'); // 回到上一个时刻
  }
  return end.diff(baseTime, 'minute');
};

const DurationSelector = ({
  value,
  onChange,
  baseTime,
  onReset,
}: {
  value: number;
  onChange: (value: number) => void;
  baseTime: Dayjs;
  onReset?: () => void;
}) => {
  const [initValue] = useState(value);
  const inputProps = {
    value,
    emptyValueOnOpen: true,
    inputProps: {
      style: {
        display: 'inline-block',
        color: 'red',
        width: 70,
        textAlign: 'center',
      },
      min: 1,
    },
    keyboardProps: {
      parseValueToNumber: true,
      precision: 0,
      showAllClean: true,
    },
    onChange: v => {
      onChange(v as number);
    },
  } as const;

  const shortcut = (forward: boolean) => {
    onChange(stepMinutes(baseTime, forward, value, 15));
  };

  return (
    <Space size={5}>
      <div />
      <PosButton
        icon={<ReloadOutlined />}
        onClick={() => {
          onChange(initValue);
          onReset?.();
        }}
      />
      <PosButton
        icon={<MinusOutlined />}
        onClick={() => {
          shortcut(false);
        }}
      />
      <NumericKeyboardInput {...inputProps} />
      <PosButton
        icon={<PlusOutlined />}
        onClick={() => {
          shortcut(true);
        }}
      />
      <div />
    </Space>
  );
};

export const ConfirmModal = ({
  confirmInfo,
  onClose,
  onConfirmed,
}: {
  confirmInfo?: {
    appointment?: Appointment;
    preOrder: PreOrder;
  };
  onClose: () => void;
  onConfirmed: (appointment: Appointment, preOrder: PreOrder, userFullInfo: UserFullInfo, baseTime: Dayjs) => void;
}) => {
  const [baseTime, setBaseTime] = useState(dayjs());
  const [endTime, setEndTime] = useState<Dayjs>();
  const [duration, setDuration] = useState<number>(undefined);
  const [saving, setSaving] = useState(false);
  const {
    payload: userFullInfo,
    send: requestUserFullInfo,
    reset: resetUserInfo,
    loading: userFullInfoLoading,
    setPayload: setUserFullInfo,
  } = useUserFullInfo();

  useEffect(() => {
    if (confirmInfo) {
      setDuration(getTotalDuratonBySKUSpecifications(confirmInfo.preOrder?.specifications));
      // 如果有预约，则从预约信息中获取 userId 并拉取用户信息
      if (confirmInfo.appointment?.userId > 0) {
        requestUserFullInfo({ userId: confirmInfo.appointment?.userId }).catch(() => {
          message.error('拉取用户信息失败，请关闭弹窗重试');
        });
      } else if (confirmInfo.preOrder.userFullInfo) {
        // 否则如果预订单里有用户信息，则直接使用
        setUserFullInfo(() => confirmInfo.preOrder.userFullInfo);
      }
    }
  }, [confirmInfo]);

  useEffect(() => {
    if (duration) {
      setEndTime(baseTime.clone().add(duration, 'minute').subtract(1, 'minute').second(59));
    }
  }, [duration]);

  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setBaseTime(dayjs());
  //   }, 1000);
  //   return () => {
  //     clearInterval(timer);
  //   };
  // }, []);

  const onReset = () => {
    setBaseTime(dayjs());
  };

  if (!confirmInfo || duration === undefined) {
    return null;
  }

  const { appointment, preOrder } = confirmInfo;

  return (
    <Modal
      afterClose={() => {
        resetUserInfo();
      }}
      okButtonProps={{
        loading: saving,
        disabled: appointment?.userId > 0 && !userFullInfo,
      }}
      onOk={async () => {
        playSound('click');
        setSaving(true);
        try {
          let savedAppointment: Appointment = null;
          if (!appointment) {
            // 由于预约时间以每整30分钟的时间戳作为 key， 因此把 startTime 设定为过去的时间里最近的一个整30分钟
            const backforwardMinutes = stepMinutes(baseTime, false, 0, 30);
            const startTime = baseTime.clone().add(backforwardMinutes, 'minute').second(0);

            // 创建预约占位
            savedAppointment = await startService({
              startTime: startTime.format(DateTimeFormat),
              endTime: endTime.format(DateTimeFormat),
              checkinTime: baseTime.format(DateTimeFormat),
              // 一个服务位只能对应1个客户
              count: 1,
              orderSn: preOrder.orderSn,
            });
          } else {
            // 更新已有预约的信息: 时间 / 状态
            savedAppointment = await startService({
              id: appointment.id,
              checkinTime: baseTime.format(DateTimeFormat),
              endTime: endTime.format(DateTimeFormat),
              orderSn: preOrder.orderSn,
            });
          }
          message.success('操作成功，请开始服务');
          onConfirmed(savedAppointment, preOrder, userFullInfo, baseTime.clone());
        } catch (e) {
          const err = e as APIError;
          message.error(`操作失败: ` + (err.message || err.msg));
          setSaving(false);
        } finally {
          // setSaving(false);
        }
      }}
      width={650}
      destroyOnHidden
      open={Boolean(confirmInfo)}
      title="时长确认"
      onCancel={onClose}>
      <Alert className="mb-m" type="warning" showIcon message={<>此界面操作不可逆，请谨慎操作</>} />

      <div className={'mb-m align-flex-horizontal-center'}>
        {!appointment ? (
          <>
            将创建
            {/* <NumericKeyboardInput {...inputProps} /> */}
            <DurationSelector baseTime={baseTime} value={duration} onChange={setDuration} onReset={onReset} />
            <Typography.Text strong>分钟</Typography.Text>的预约占位
          </>
        ) : (
          <>
            将在
            <DurationSelector baseTime={baseTime} value={duration} onChange={setDuration} onReset={onReset} />
            <Typography.Text strong>分钟</Typography.Text>后释放预约点位
          </>
        )}
      </div>

      <Typography.Title level={5} className="text-center">
        结束时间: {baseTime.clone().add(duration, 'minute').format(TimeMinutesFormat)}
      </Typography.Title>

      {appointment && appointment.userId ? (
        userFullInfoLoading ? (
          <Loading />
        ) : (
          <OrderDescriptions
            showAppointmentOperation={false}
            orderDetail={{
              appointment,
              order: null,
              user: userFullInfo?.user,
              skuList: null,
            }}
            postInvalidate={void 0}
          />
        )
      ) : null}
    </Modal>
  );
};
